import { useEffect } from 'react'
import { BrandMark, Wordmark } from './Brand'
import { Backdrop } from './MaintenanceScreen'

/** How often a refused screen asks again: once approved, MBI opens by itself (05 7.9). */
const RECHECK_MS = 5 * 60_000

/**
 * ACCESS DENIED (05 7.9): every refusal at the door, waiting for approval
 * included, and a phone with phone access off (default 19 kept the one message).
 *
 * DRAWN BEFORE THE SHELL, like PlatformClosed, so no menu promises doors that will
 * not open. It is not what keeps them out: the API's entry gate refuses everything
 * but /me, and deleting this branch in a debugger produces an empty shell full of
 * 403s rather than access.
 *
 * ONE SENTENCE, NOTHING ABOUT WHY (03 r4). The signed-in address is shown because
 * it catches the commonest real cause, signing in with another account; it is the
 * reader's own, not a reason. The reference joins a support call to the log.
 *
 * WHILE AN ADMINISTRATOR IS SIGNED IN AS THEM: the same page, plus one line only
 * they see, in the server's own words, and the way back out.
 */
export function AccessDenied({
  user,
  reference,
  why,
  phone,
  viewAs,
  onRecheck,
  onSignOut,
  onExitViewAs,
  onOpenAccess,
}: {
  /** Who is really signed in. */
  user: string | null
  /** /me's correlation id. */
  reference: string
  /** Why, for an administrator signed in as the person; null for everyone else. */
  why: string | null
  /**
   * Refused for the phone. The message stays the one sentence (default 19); only
   * the promise that MBI opens by itself goes, which this device would never keep.
   */
  phone: boolean
  /** Whose account is being used, under Login As. */
  viewAs: string | null
  onRecheck: () => void
  onSignOut: () => void
  onExitViewAs: () => void
  /** Stop signing in as them and open Access, where their page is. */
  onOpenAccess: () => void
}) {
  // Every five minutes and whenever the tab comes back: an approval made while
  // they waited opens MBI without anybody thinking to reload.
  useEffect(() => {
    const id = setInterval(onRecheck, RECHECK_MS)
    const onFocus = () => onRecheck()
    const onVisible = () => {
      if (document.visibilityState === 'visible') onRecheck()
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearInterval(id)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [onRecheck])

  return (
    <div className="on-navy relative grid h-dvh place-items-center overflow-y-auto bg-(--color-navy-800) px-6 py-10 text-white">
      <Backdrop />

      <main className="relative w-full max-w-xl">
        <div className="anim-rise flex items-center gap-3">
          <BrandMark size={34} tone="white" animate />
          <Wordmark tone="white" subtitle="" />
        </div>

        <h1
          className="anim-rise mt-10 text-[38px] font-semibold leading-[1.1] tracking-[-0.025em] text-white"
          style={{ animationDelay: '80ms' }}
        >
          Access denied
        </h1>

        <div
          aria-hidden
          className="anim-draw mt-6 h-[3px] w-24 rounded-full bg-white/60"
          style={{ animationDelay: '340ms' }}
        />

        <p
          className="anim-rise mt-7 max-w-lg text-[17px] font-light leading-relaxed text-white/80"
          style={{ animationDelay: '160ms' }}
        >
          Contact your MBI administrator.
        </p>

        <div
          className="anim-rise mt-8 flex flex-wrap items-center gap-x-4 gap-y-3"
          style={{ animationDelay: '220ms' }}
        >
          <button
            type="button"
            onClick={onRecheck}
            className="inline-flex h-10 items-center justify-center gap-2 rounded border border-transparent bg-white px-5 text-(length:--text-base) font-medium text-(--color-navy-800) transition-all duration-150 hover:-translate-y-px hover:bg-white/90 active:translate-y-0"
          >
            Try again
          </button>
          <p aria-live="polite" className="text-(length:--text-base) text-white/65">
            {!phone && 'MBI opens by itself once you have access. '}
            <span className="text-white/55">Checking every 5 minutes.</span>
          </p>
        </div>

        {viewAs && (
          <div
            className="anim-rise mt-9 rounded-md border border-(--color-privileged)/45 bg-(--color-privileged)/15 px-4 py-3.5"
            style={{ animationDelay: '280ms' }}
          >
            <p className="text-(length:--text-body) leading-relaxed text-white/80">
              You are signed in as <span className="font-semibold text-white">{viewAs}</span>, so you are seeing what
              they see.
            </p>
            {why && (
              <p className="mt-2 text-(length:--text-body) leading-relaxed text-white">
                <span className="font-semibold">Why (only you can see this):</span> {why}
              </p>
            )}
            <div className="mt-2.5 flex flex-wrap gap-x-5 gap-y-1">
              <button
                type="button"
                onClick={onOpenAccess}
                className="rounded text-(length:--text-body) font-semibold text-white underline underline-offset-4 hover:text-white/80"
              >
                Stop, and open Access
              </button>
              <button
                type="button"
                onClick={onExitViewAs}
                className="rounded text-(length:--text-body) text-white/75 underline underline-offset-4 hover:text-white"
              >
                Stop sign in as
              </button>
            </div>
          </div>
        )}

        <div
          className="anim-rise mt-10 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-white/12 pt-5 text-(length:--text-small) text-white/65"
          style={{ animationDelay: '340ms' }}
        >
          {/* At 65% white, not PlatformClosed's 40%: small text on this navy needs
              4.5:1, and 40% gave about 3.3 (review, 27 Oct 2026). */}
          {user && (
            <span>
              Signed in as <span className="text-white/85">{user}</span>.
              <span aria-hidden className="text-white/30">
                {' · '}
              </span>
            </span>
          )}
          <span>
            Reference <span className="font-mono text-white/85">{reference}</span>
            <span aria-hidden className="text-white/30">
              {' · '}
            </span>
          </span>
          <button type="button" onClick={onSignOut} className="rounded underline underline-offset-4 hover:text-white">
            Sign out
          </button>
        </div>
      </main>
    </div>
  )
}
